import styled from 'styled-components';

import { IconHourglass } from '@components/icons/hourglass';
import { RelativeSpinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { hourList, minuteList } from '@utils/number-arrays';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import {
  ActionFooter,
  ActionHeader,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
} from 'ui/journey-builder/node-types/common';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { getWaitForTimeLength } from '../common';

type WaitForData = {
  days: string;
  hours: string;
  minutes: string;
};

const Form = createActionForm<WaitForData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<'wait_for_time'>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();

  const days = (formStore.useFieldValue('days') ?? '') as string;
  const hours = (formStore.useFieldValue('hours') ?? '') as string;
  const minutes = (formStore.useFieldValue('minutes') ?? '') as string;
  const timeLengthText = getWaitForTimeLength(days, hours, minutes);

  const values = data?.values;
  const editMutation = useUpdateJourneyAction(journeyActionId);

  const handleSave = (data: WaitForData) =>
    editMutation.mutateAsync({
      action: 'wait_for_time',
      isAutomatic: true,
      values: {
        days: { value: data.days },
        hours: { value: data.hours },
        minutes: { value: data.minutes },
      },
      description: null,
    });

  if (isLoading || !data) {
    return <RelativeSpinner />;
  }

  return (
    <Sidebar>
      <ActionHeader
        title={t('waitforDays')}
        icon={IconHourglass}
        iconColor="orange100"
        {...props}
      />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
          <Stack space={6}>
            <Stack space={4}>
              <Text text={t('selectHowLong')} font="regular14" />
              <Text text={t('useWhen')} font="regular14" />
            </Stack>
            <Separator />
            <FormGroupContainer>
              <Form.TextField
                id="days"
                label={t('days')}
                defaultValue={values?.days?.value ?? '0'}
                validators={['number']}
              />
              <Form.Select
                id="hours"
                label={t('hours')}
                options={hourList}
                placeholder=""
                defaultValue={values?.hours?.value ?? '0'}
                isClearable={false}
              />
              <Form.Select
                id="minutes"
                label={t('minutes')}
                options={minuteList}
                placeholder=""
                defaultValue={values?.minutes?.value ?? '0'}
                isClearable={false}
              />
            </FormGroupContainer>
            <Text
              text={timeLengthText.length > 0 ? t('waitsForDescription', { timeLengthText }) : ''}
            />
            <ActionFooter
              action={data}
              isDisabled={!isDirty || editMutation.isLoading}
              form={Form}
            />
          </Stack>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    waitforDays: 'Wait for #',
    selectHowLong: 'Select for how long journey should wait before triggering the following action',
    useWhen: 'Use when you want to pause by a defined amount of time between actions in a journey',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    waitsFor: 'Waits for',
    day: 'Day',
    hour: 'Hour',
    minute: 'Minute',
    waitsForDescription: 'Waits for {timeLengthText} before triggering the following action',
  },
};

export const FormGroupContainer = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
})({
  flexDirection: 'column',
  '> *': {
    flex: '1 1',
  },
  [theme.mediaBreakpoint.md]: {
    flexDirection: 'row',
  },
});
